// SeoDetailModuleComponent.js
import React, { useState } from 'react';
import { Box, Typography, IconButton, Collapse } from '@mui/material';
import ScrollFadeWrapper from '../../ScrollFadeWrapper/ScrollFadeWrapper';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Image from '../../Image/Image';

const KEYPOINT_VECTOR_URL =
  'https://assets.askhippo.co.za/Vectors/KeyPointVector.svg';
const TICK_ICON_URL =
  'https://assets.askhippo.co.za/Icons/common/TickCircle.svg';
const CROSS_ICON_URL =
  'https://assets.askhippo.co.za/Icons/common/Delete-Square-New.svg';
const TABLE_MIN_WIDTH = '720px';
const CARD_SECTION_GAP = '32px';
const HEADER_SECTION_GAP = '16px';
const MODULE_SECTION_GAP = '40px';
const IMAGE_CAPTION_GAP = '16px';

const SeoDetailModuleComponent = ({ data }) => {
  const { title, description, footer, cards = [], faq = [] } = data || {};
  const hasFAQ = Array.isArray(faq) && faq.length > 0;
  const html = (value) => ({ __html: typeof value === 'string' ? value : '' });

  const [openIndex, setOpenIndex] = useState(null);
  const toggle = (index) => setOpenIndex(index === openIndex ? null : index);

  const CELL_TEXT_SX = {
    color: '#162F3B',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
    letterSpacing: '-0.002em',
    m: 0,
    width: '100%',
    textAlign: 'center',
  };

  const IconCell = ({ icon }) => (
    <Image
      width={20}
      height={20}
      src={icon === 'check' ? TICK_ICON_URL : CROSS_ICON_URL}
      alt={icon === 'check' ? 'Yes' : 'No'}
      sx={{ width: '20px', height: '20px', display: 'block', margin: '0 auto' }}
    />
  );

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          width: '802px',
          backgroundColor: '#E3E6E8',
          borderRadius: '48px',
          pt: '52px',
          px: '32px',
          pb: '32px',
          gap: MODULE_SECTION_GAP,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: HEADER_SECTION_GAP,
            width: '100%',
          }}
        >
          {/* Title */}
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '3rem',
              lineHeight: '3.25rem',
              letterSpacing: '-0.03em',
              color: '#162F3B',
              textAlign: 'center',
              whiteSpace: 'pre-line',
            }}
            component="div"
            dangerouslySetInnerHTML={html(title)}
          />

          {/* Description */}
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: '1.5rem',
              lineHeight: '1.75rem',
              letterSpacing: '-0.03em',
              color: '#162F3B',
              textAlign: 'center',
              maxWidth: '738px',
              whiteSpace: 'pre-line',
            }}
            component="div"
            dangerouslySetInnerHTML={html(description)}
          />
        </Box>

        {/* Cards */}
        {cards.map((card, index) => (
          <ScrollFadeWrapper key={index} delay={index * 0.1}>
            <Box
              sx={{
                width: '1010px',
                backgroundColor: '#FFFFFF',
                borderRadius: '48px',
                boxShadow:
                  '0px 1px 2px 0px #A2ACB133, 0px 12px 24px -4px #162F3B1F',
                boxSizing: 'border-box',
                p: '40px',
                display: 'flex',
                gap: '40px',
              }}
            >
              {/* Number Icon */}
              <Box
                sx={{
                  minWidth: '100px',
                  display: 'flex',
                  alignItems: 'flex-start',
                }}
              >
                <Box
                  sx={{ position: 'relative', width: '96px', height: '96px' }}
                >
                  <Image
                    width={96}
                    height={96}
                    src={KEYPOINT_VECTOR_URL}
                    alt="Key point illustration"
                    sx={{ width: '100%', height: '100%' }}
                  />
                  <Typography
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      fontFamily: 'Pally, Inter',
                      letterSpacing: '-0.03em',
                      fontWeight: 700,
                      fontSize: '4.5rem',
                      lineHeight: '4.25rem',
                      color: '#162F3B',
                      pointerEvents: 'none',
                    }}
                  >
                    {card.number}
                  </Typography>
                </Box>
              </Box>

              {/* Card Content */}
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  maxWidth: '730px',
                  minWidth: 0,
                  gap: CARD_SECTION_GAP,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '1.5rem',
                    lineHeight: '1.75rem',
                    color: '#162F3B',
                  }}
                  component="div"
                  dangerouslySetInnerHTML={html(card.title)}
                />

                <Box
                  sx={{
                    fontWeight: 400,
                    fontSize: '1rem',
                    lineHeight: '1.5rem',
                    color: '#162F3B',
                    whiteSpace: 'pre-line',
                    '& p': { m: 0, mb: '1em' },
                    '& p:last-of-type': { mb: 0 },
                    '& ul': { m: 0, mt: '1em', pl: '20px' },
                    '& li': { mb: '0.5em' },
                  }}
                  component="div"
                  dangerouslySetInnerHTML={html(card.description)}
                />

                {/* Image block */}
                {card.image && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: IMAGE_CAPTION_GAP,
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: '#F3F6F6',
                        height: '303px',
                        borderRadius: '16px',
                        width: '100%',
                      }}
                    />
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        lineHeight: '1.25rem',
                        color: '#162F3B',
                      }}
                    >
                      {card.caption}
                    </Typography>
                  </Box>
                )}

                {/* Table block */}
                {card.table && (
                  <Box
                    sx={{
                      width: '100%',
                      borderRadius: '16px',
                      border: '1px solid #E3E6E8',
                      overflowX: 'auto',
                      WebkitOverflowScrolling: 'touch',
                      boxSizing: 'border-box',
                    }}
                  >
                    <table
                      style={{
                        width: '100%',
                        minWidth: TABLE_MIN_WIDTH,
                        borderCollapse: 'collapse',
                        tableLayout: 'auto',
                      }}
                    >
                      {card.table.headers && card.table.headers.length > 0 && (
                        <thead>
                          <tr style={{ backgroundColor: '#C7CA00' }}>
                            {card.table.headers.map((header, i) => (
                              <th
                                key={i}
                                style={{
                                  padding: '10px 8px',
                                  textAlign: 'center',
                                  verticalAlign: 'middle',
                                  fontFamily: 'Inter, sans-serif',
                                  fontWeight: 600,
                                  fontSize: '0.875rem',
                                  lineHeight: '1.25rem',
                                  letterSpacing: '-0.02em',
                                  color: '#162F3B',
                                  borderRight: '1px solid #E3E6E8',
                                }}
                              >
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                      )}

                      <tbody>
                        {card.table.rows.map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            {row.map((cell, colIndex) => (
                              <td
                                key={colIndex}
                                style={{
                                  padding: '10px 8px',
                                  textAlign: 'center',
                                  verticalAlign: 'middle',
                                  borderTop: '1px solid #E3E6E8',
                                  borderRight: '1px solid #E3E6E8',
                                }}
                              >
                                {typeof cell === 'object' && cell.icon ? (
                                  <IconCell icon={cell.icon} />
                                ) : (
                                  <Typography sx={CELL_TEXT_SX}>
                                    {cell}
                                  </Typography>
                                )}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                )}

                {card.strongText && (
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: '1rem',
                      lineHeight: '1.5rem',
                      letterSpacing: '-0.02em',
                      color: '#162F3B',
                    }}
                    component="div"
                    dangerouslySetInnerHTML={html(card.strongText)}
                  />
                )}

                {card.paragraph && (
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontSize: '1rem',
                      lineHeight: '1.5rem',
                      letterSpacing: '-0.02em',
                      color: '#162F3B',
                      whiteSpace: 'pre-line',
                    }}
                    component="div"
                    dangerouslySetInnerHTML={html(card.paragraph)}
                  />
                )}
              </Box>
            </Box>
          </ScrollFadeWrapper>
        ))}

        {/* FAQ */}
        {hasFAQ && (
          <Box
            sx={{
              width: '1010px',
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
            }}
          >
            {faq.map((item, i) => {
              const isOpen = openIndex === i;
              return (
                <Box
                  key={i}
                  role="button"
                  tabIndex={0}
                  onClick={() => toggle(i)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggle(i);
                    }
                  }}
                  sx={{
                    width: '100%',
                    backgroundColor: '#FFFFFF',
                    borderRadius: '24px',
                    boxShadow:
                      '0px 1px 2px 0px #A2ACB133, 0px 12px 24px -4px #162F3B1F',
                    p: '16px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 700,
                        fontSize: '1.25rem',
                        lineHeight: '1.5rem',
                        letterSpacing: '-0.03em',
                        color: '#162F3B',
                      }}
                    >
                      {item.title}
                    </Typography>

                    <IconButton
                      disableRipple
                      tabIndex={-1}
                      sx={{
                        width: '32px',
                        height: '32px',
                        color: '#162F3B',
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 200ms ease',
                      }}
                      aria-label={isOpen ? 'Collapse' : 'Expand'}
                    >
                      <ArrowDropDownIcon sx={{ fontSize: '32px' }} />
                    </IconButton>
                  </Box>

                  <Collapse in={isOpen} timeout={250} unmountOnExit>
                    <Typography
                      sx={{
                        mt: '16px',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 400,
                        fontSize: '1rem',
                        lineHeight: '1.5rem',
                        letterSpacing: '-0.02em',
                        color: '#162F3B',
                        whiteSpace: 'pre-line',
                      }}
                    >
                      {item.description}
                    </Typography>
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Footer */}
        <Typography
          sx={{
            textAlign: 'center',
            fontFamily: 'Inter, sans-serif',
            fontSize: '1rem',
            fontWeight: 400,
            lineHeight: '1.5rem',
            letterSpacing: '-0.02em',
            color: '#162F3B',
            maxWidth: '473px',
          }}
        >
          {footer}
        </Typography>
      </Box>
    </Box>
  );
};

export default SeoDetailModuleComponent;
